/**
 * "O que quebra se eu mexer aqui", portada de knowledge.js:512-594.
 *
 * Os dependentes vêm do grafo do AST, não de palpite do modelo — é o que separa
 * esta tela de uma busca por texto. Mesma resposta que a tool `impact_of` dá ao
 * agente no Cursor, de propósito: painel e agente divergindo sobre o que quebra
 * é o jeito mais rápido de o time parar de confiar nos dois.
 *
 * O que muda: a unidade e a profundidade estão na URL (`?key=`, `?depth=`), para
 * o "olha o que isso atinge" poder ser colado num code review.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDocs, useImpacto } from '../api/consultas';
import { acionavel } from '../componentes/acionavel';
import { Carregando, Vazio } from '../componentes/Estado';
import { copiar, useRecado } from '../componentes/Recado';

export function Impacto({ slug }: { slug: string }) {
  const [parametros, setParametros] = useSearchParams();
  const chave = parametros.get('key') || '';
  const profundidade = Number(parametros.get('depth')) || 2;
  const [rascunho, setRascunho] = useState(chave);
  const docs = useDocs(slug);

  // chegar aqui por link ou pelo botão "ver impacto" tem de preencher o campo
  useEffect(() => setRascunho(chave), [chave]);

  const analisar = (k: string, p: number) => {
    const novo = new URLSearchParams(parametros);
    if (k) novo.set('key', k); else novo.delete('key');
    novo.set('depth', String(p));
    setParametros(novo);
  };

  const arquivos = (docs.data || []).filter((d) => d.unit_kind === 'file');

  return (
    <>
      <div className="crumb"><b>{slug}</b><span className="sep">/</span>impacto</div>
      <div className="sec-title">O que quebra se eu mexer aqui</div>
      <div className="sec-sub">
        Dependentes vêm do grafo do AST (não de palpite), as rotas são os endpoints alcançados, e o
        dono e as decisões dizem com quem falar e o que respeitar. Mesma resposta que a tool{' '}
        <code>impact_of</code> devolve ao agente.
      </div>
      <div className="imp-in">
        <label className="sr-only" htmlFor="imp-q">unidade a analisar</label>
        <input
          id="imp-q"
          list="imp-list"
          placeholder="unit_key do arquivo ou nó do grafo"
          value={rascunho}
          onChange={(e) => setRascunho(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') analisar(rascunho.trim(), profundidade); }}
        />
        <datalist id="imp-list">
          {arquivos.map((d) => <option key={d.unit_key} value={d.unit_key} />)}
        </datalist>
        <label className="sr-only" htmlFor="imp-depth">profundidade</label>
        <select
          id="imp-depth"
          value={profundidade}
          onChange={(e) => analisar(rascunho.trim(), Number(e.target.value))}
        >
          {[1, 2, 3, 4].map((n) => <option key={n} value={n}>profundidade {n}</option>)}
        </select>
        <button type="button" className="btn" onClick={() => analisar(rascunho.trim(), profundidade)}>
          Analisar
        </button>
      </div>

      <div id="imp-out">
        {chave
          ? <Resultado slug={slug} chave={chave} profundidade={profundidade} />
          : (
            <Vazio
              titulo="Escolha um arquivo para ver o estrago"
              comando="eng impact src/messages/dispatch.service.ts"
            >
              Digite o caminho de um arquivo (ou escolha na lista) e o painel mostra quem depende
              dele, quais rotas ele alcança, quem é o dono e quais decisões o governam — tudo tirado
              do grafo do AST, não de suposição.
            </Vazio>
          )}
      </div>
    </>
  );
}

function Resultado({ slug, chave, profundidade }: { slug: string; chave: string; profundidade: number }) {
  const avisar = useRecado();
  const navegar = useNavigate();
  const docs = useDocs(slug);
  const consulta = useImpacto(slug, chave, profundidade);

  if (consulta.isPending) return <Carregando rotulo={`percorrendo o grafo a partir de ${chave}…`} />;
  // "não achei este nó" é resposta da rota, não queda do servidor: o painel
  // atual explicava onde conferir o caminho em vez de mostrar erro cru
  if (consulta.error) {
    const msg = consulta.error instanceof Error ? consulta.error.message : String(consulta.error);
    return (
      <Vazio titulo="Não achei este nó no acervo">
        <b>{chave}</b> não bate com nenhum arquivo documentado nem com nó do grafo ({msg}). Confira o
        caminho completo na barra lateral — em geral começa com <code>src/</code>.
      </Vazio>
    );
  }

  const r = consulta.data!;
  const ir = (k: string) => {
    if ((docs.data || []).some((d) => d.unit_key === k) || k.startsWith('__')) {
      navegar(`/p/${slug}/doc?key=${encodeURIComponent(k)}`);
      return;
    }
    navegar(`/p/${slug}/graph?no=${encodeURIComponent(k)}`);
  };

  const Linha = ({ k, nivel, extra }: { k: string; nivel?: number | null; extra?: string | null }) => (
    <div className="row">
      <code title={k} {...acionavel(() => ir(k))}>{k}</code>
      {!!nivel && <span className="lvl">nível {nivel}</span>}
      {!!extra && <span className="lvl">{extra}</span>}
    </div>
  );

  return (
    <>
      <div className="hcards">
        <div className={`hcard${r.dependent_files.length ? ' alert' : ''}`}>
          <div className="n">{r.dependent_files.length}</div><div className="l">arquivos dependentes</div>
        </div>
        <div className={`hcard${r.routes.length ? ' alert' : ''}`}>
          <div className="n">{r.routes.length}</div><div className="l">rotas atingidas</div>
        </div>
        <div className="hcard">
          <div className="n">{r.decisions.length}</div><div className="l">decisões que citam</div>
        </div>
        <div className="hcard">
          <div className="n">{r.notes}</div><div className="l">notas do time</div>
        </div>
      </div>

      {r.owner.orphan && (
        <div className="warn-line">
          Ninguém declarado como dono — se quebrar, não há endereço para a pergunta.
        </div>
      )}
      {r.dirty_locally && (
        <div className="warn-line">
          Há versão não commitada deste arquivo indexada no acervo (working tree).
        </div>
      )}

      <div className="imp-cols">
        <div className="imp-card">
          <h4>A quem perguntar</h4>
          <div className="row"><b>dono</b><code>{r.owner.ask || '— não declarado'}</code></div>
          {(r.owner.reviewers || []).map((x) => (
            <div className="row" key={x.email}><b>revisor</b><code>{x.email}</code></div>
          ))}
          {(r.owner.note_authors || []).map((e) => (
            <div className="row" key={e}><b>nota recente</b><code>{e}</code></div>
          ))}
          {r.owner.last_commit && (
            <div className="row">
              <b>último commit</b>
              <code>
                {r.owner.last_commit.author || '—'} · {(r.owner.last_commit.date || '').slice(0, 10)}
              </code>
            </div>
          )}
        </div>

        <div className="imp-card">
          <h4>Rotas atingidas</h4>
          {r.routes.length
            ? r.routes.slice(0, 20).map((x) => <Linha key={x.node_key} k={x.node_key} extra={x.why} />)
            : <div className="muted" style={{ fontSize: 12.5 }}>nenhuma rota alcançada</div>}
        </div>

        <div className="imp-card">
          <h4>Dependentes (arquivos)</h4>
          {r.dependent_files.length
            ? r.dependent_files.slice(0, 25).map((f) => {
              const d = r.dependents.find((x) => x.node_key === f);
              return <Linha key={f} k={f} nivel={d?.level} extra={d?.via} />;
            })
            : <div className="muted" style={{ fontSize: 12.5 }}>nada depende deste arquivo pelo AST</div>}
        </div>

        <div className="imp-card">
          <h4>Decisões a respeitar</h4>
          {r.decisions.length
            ? r.decisions.map((d) => <Linha key={d.unit_key} k={d.unit_key} extra={d.title} />)
            : (
              <div className="muted" style={{ fontSize: 12.5 }}>
                nenhuma decisão registrada cita este arquivo
              </div>
            )}
        </div>
      </div>

      <div className="prop-acts" style={{ marginTop: 14, border: 'none' }}>
        <button
          type="button"
          className="btn ghost sm"
          onClick={() => navegar(`/p/${slug}/doc?key=${encodeURIComponent(r.unit_key)}`)}
        >
          abrir doc
        </button>
        <button
          type="button"
          className="btn ghost sm"
          onClick={() => navegar(`/p/${slug}/graph?no=${encodeURIComponent(r.unit_key)}`)}
        >
          ver no grafo
        </button>
        <button
          type="button"
          className="btn ghost sm"
          onClick={async () => {
            avisar(await copiar(`eng impact ${r.unit_key} --depth ${profundidade}`)
              ? 'comando copiado'
              : 'não consegui copiar — selecione o texto e use Ctrl+C');
          }}
        >
          copiar <code>eng impact</code>
        </button>
      </div>
    </>
  );
}
